import React, { MutableRefObject, useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import MyAlert from "@common-utils/MyAlert"
import {
     CONST_CANDIDAT_EXPERIENCES_OPTIONS_FOR_SELECT2,
     CONST_CANDIDAT_SUIVI_OPTIONS,
     CONST_CANDIDAT_TYPE_SAISI,
     CONST_CANDIDAT_TYPES_FOR_SELECT2,
     CONST_DISPONIBILITE_DATE_EXACTE,
     CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MOBILITE_DEPARTEMENTAL,
     CONST_MOBILITE_OPTIONS_FOR_SELECT2,
     CONST_MOBILITE_PAR_VILLE,
     CONST_MOBILITE_REGIONAL,
     CONST_NIVEAU_ETUDES_OPTIONS_FOR_SELECT2,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_FRAICHEURS_CV_OPTIONS_FOR_SELECT2,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORD_TARGETS_OPTIONS_FOR_SELECT2,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORDTARGET_CV,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORDTARGET_NOM_MAIL_TEL,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_OPERATEUR_LOGIQUE_OU,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_OPERATEURS_LOGIQUE_OPTIONS_FOR_SELECT2,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_TYPE_SAUVEGARDE,
     CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_VISUALISATIONS_OPTIONS_FOR_SELECT2,
} from "@common-constants/*"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField" // Kept (No FormField version provided)
import {
     CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT,
     CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT,
     CONST_API_UTILS_GET_CANDIDATES_URL_ENDPOINT,
     CONST_API_UTILS_GET_DEPARTEMENTS_URL_ENDPOINT,
     CONST_API_UTILS_GET_METIERS_URL_ENDPOINT,
     CONST_API_UTILS_GET_OFFERS_ENDPOINT,
     CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT,
     CONST_API_UTILS_GET_VILLES_URL_ENDPOINT,
     CONST_API_UTILS_GET_VIVIERS_ENDPOINT,
} from "../../../../endpoints"
import { toast } from "react-toastify"
import { IHttpErrorResponseModel } from "@common-models/*"
import { FormikProps } from "formik/dist/types"
import useSwal from "@common-hooks/useSwal"
import { AxiosError } from "axios"
import { ILogOrSavedFiltersSearchCandidateModel, ISearchCandidatesFiltersModel } from "./core/_models"
import { createSavedFiltersSearchCandidate, updateSavedFiltersSearchCandidate } from "./core/_requests"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"

// New Component Imports
import MySelectCreatableFormField from "@common-components/fields/MySelectCreatableFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MySelectSearchDataFromServerFormField from "@common-components/fields/MySelectSearchDataFromServerFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyDateDayMonthYearFormField from "@common-components/fields/MyDateDayMonthYearFormField"
import clsx from "clsx"

interface IFormRechercheCandidatProps {
     pInialValues?: ILogOrSavedFiltersSearchCandidateModel
     handleSubmitCallback: (values: ISearchCandidatesFiltersModel, helpers: FormikHelpers<ISearchCandidatesFiltersModel>) => void
     handleChangeKeywordsField?: (value) => void
     callBackOnReset?: () => void
     handleSaveFiltersCallback?: () => void
     formRef?: MutableRefObject<any>
}

const FormRechercheCandidat = ({
     pInialValues = {},
     handleSubmitCallback,
     callBackOnReset = () => {},
     handleChangeKeywordsField = (value: string[]) => {},
     handleSaveFiltersCallback = () => {},
     formRef,
}: IFormRechercheCandidatProps) => {
     const { swal } = useSwal()
     const moreFiltersDivScrollTo = useRef<HTMLDivElement | null>(null)
     const lessFiltersDivScrollTo = useRef<HTMLDivElement | null>(null)
     const [moreFiltersVisible, setMoreFiltersVisible] = useState<boolean>(false)
     const [isSavingFilters, setIsSavingFilters] = useState<boolean>(false)

     // Scroll to "more filters" hidden container.
     useEffect(() => {
          if (moreFiltersVisible) {
               moreFiltersDivScrollTo.current?.scrollIntoView({ behavior: "smooth" })
          } else {
               lessFiltersDivScrollTo.current?.scrollIntoView({ behavior: "smooth" })
          }
     }, [moreFiltersVisible])

     // Validation schema
     const schema = Yup.object().shape({
          searchKeywords: Yup.array().nullable().label(`"Mots clé"`),
          searchScope: Yup.string()
               .when("searchKeywords", {
                    is: val => val && val.length > 0,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Périmètre de recherche"`),
          searchOperateurLogiqueOnlyScopeCurriculum: Yup.string()
               .when("searchScope", {
                    is: CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORDTARGET_CV,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Opérateur logique"`),
          disponibilite: Yup.string().nullable().label(`"Disponibilité"`),
          disponibiliteDate: Yup.string()
               .when("disponibilite", {
                    is: CONST_DISPONIBILITE_DATE_EXACTE,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Date de disponibilité"`),
          mobilite: Yup.string().nullable().label(`"Mobilité"`),
          mobiliteRegions: Yup.array()
               .when("mobilite", {
                    is: CONST_MOBILITE_REGIONAL,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Régions"`),
          mobiliteDepartements: Yup.array()
               .when("mobilite", {
                    is: CONST_MOBILITE_DEPARTEMENTAL,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Départements"`),
          mobiliteVilles: Yup.array()
               .when("mobilite", {
                    is: CONST_MOBILITE_PAR_VILLE,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Villes"`),
          niveauEtudes: Yup.string().nullable().label(`"Niveau d'études"`),
          fraicheurCV: Yup.string().nullable().label(`Fraîcheur du CV`),
          viviers: Yup.array().nullable().label(`viviers`),
          annonces: Yup.array().nullable().label(`annonces`),
          candidatType: Yup.string().nullable().label(`"Type de candidat"`),
          personnelsAyantSaisiLeCandidat: Yup.array()
               .when("candidatType", {
                    is: CONST_CANDIDAT_TYPE_SAISI,
                    then: schema => schema.required(),
               })
               .nullable()
               .label(`"Personnes ayant saisi le candidat"`),
          secteursActivite: Yup.array().nullable().label(`"Secteurs d'activité"`),
          metiers: Yup.array().nullable().label(`"Métiers"`),
          seenOrNot: Yup.string().nullable().label(`"Vu"`),
          experienceMin: Yup.string().nullable().label(`"Expérience min."`),
     })

     // Initial values
     const initialValues: ISearchCandidatesFiltersModel = {
          searchScope: pInialValues.searchScope || CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORDTARGET_NOM_MAIL_TEL,
          searchOperateurLogiqueOnlyScopeCurriculum:
               pInialValues.searchOperateurLogiqueOnlyScopeCurriculum || CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_OPERATEUR_LOGIQUE_OU,
          searchKeywords: pInialValues.searchKeywords,
          disponibilite: pInialValues.disponibilite,
          disponibiliteDate: pInialValues.disponibiliteDate?.format("YYYY-MM-DD"),
          mobilite: pInialValues.mobilite,
          mobiliteRegions: pInialValues.mobiliteRegions?.map(item => item.id),
          mobiliteDepartements: pInialValues.mobiliteDepartements?.map(item => item.id),
          mobiliteVilles: pInialValues.mobiliteVilles?.map(item => item.id as number),
          niveauEtudes: pInialValues.niveauEtudes,
          fraicheurCV: pInialValues.fraicheurCV,
          viviers: pInialValues.viviers?.map(item => item.id as number),
          annonces: pInialValues?.annonces?.map(item => item.id as number),
          candidats: pInialValues?.candidats?.map(item => item.id as number),
          areCandidatesInListeDeTraitement: !!pInialValues?.areCandidatesInListeDeTraitement,
          isAvecSuivi: !!pInialValues?.isAvecSuivi,
          avecSuiviType: pInialValues?.avecSuiviType,
          avecSuiviPersonnels: pInialValues?.avecSuiviPersonnels?.map(item => item.id as number),
          avecSuiviDateDebut: pInialValues?.avecSuiviDateDebut?.format("YYYY-MM-DD"),
          avecSuiviDateFin: pInialValues?.avecSuiviDateFin?.format("YYYY-MM-DD"),
          candidatType: pInialValues.candidatType,
          personnelsAyantSaisiLeCandidat: pInialValues.personnelsAyantSaisiLeCandidat?.map(item => item.id as number),
          secteursActivite: pInialValues.secteursActivite?.map(item => item.id as number),
          metiers: pInialValues.metiers?.map(item => item.id as number),
          seenOrNot: pInialValues.seenOrNot,
          experienceMin: pInialValues.experienceMin,
          ...(pInialValues?.typeHistoriqueOuSauvegarde === CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_TYPE_SAUVEGARDE
               ? {
                      titreSauvegarde: pInialValues?.titreSauvegarde,
                 }
               : {}),
          blacklistStatus: pInialValues?.blacklistStatus ?? "all", // "all" | "yes" | "no"
          blacklistPersonnels: pInialValues.blacklistPersonnels?.map(item => item.id as number) ?? [], // Ajouté
     }

     function handleSubmit(values: ISearchCandidatesFiltersModel, helpers: FormikHelpers<ISearchCandidatesFiltersModel>) {
          helpers.setStatus(null)
          handleSubmitCallback(values, helpers)
     }

     function handleSauvegarde(helpers: FormikProps<ISearchCandidatesFiltersModel>) {
          if (pInialValues?.typeHistoriqueOuSauvegarde === CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_TYPE_SAUVEGARDE && pInialValues.id) {
               helpers.setStatus(null)
               setIsSavingFilters(true)
               updateSavedFiltersSearchCandidate(pInialValues.id, helpers.values)
                    .then(() => {
                         toast.success("Filtres sauvegardés.")
                         handleSaveFiltersCallback()
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         if (error.errors && error.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR) {
                              for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                         }
                         helpers.setStatus(error?.detail)
                         toast.error(error?.detail, { autoClose: false })
                    })
                    .finally(() => setIsSavingFilters(false))
          } else {
               swal.fire({
                    icon: "info",
                    title: `Veuillez choisir un titre pour votre sauvegarde.`,
                    text: "Cette sauvegarde vous permettra d'accélérer vos prochaines recherches candidat.",
                    input: "text",
                    inputPlaceholder: "Votre titre",
                    inputAttributes: { required: "true", autoComplete: "off" },
                    showCancelButton: true,
                    confirmButtonText: "Oui",
                    cancelButtonText: "Annuler",
                    showLoaderOnConfirm: true,
                    inputValidator: value => {
                         return new Promise((resolve: any) => {
                              value === "" ? resolve("Veuillez renseigner un titre.") : resolve()
                         })
                    },
                    preConfirm: async (titre: string) => {
                         return createSavedFiltersSearchCandidate(titre, helpers.values)
                              .then(r => {
                                   helpers.setStatus(null)
                                   toast.info(r.data.message)
                                   handleSaveFiltersCallback()
                              })
                              .catch((e: AxiosError) => {
                                   const error: IHttpErrorResponseModel = e.response?.data
                                   if (error.errors && error.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR) {
                                        for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                                   }
                                   helpers.setStatus(error?.detail)
                                   toast.error(error?.detail, { autoClose: false })
                              })
                              .finally(() => setIsSavingFilters(false))
                    },
                    allowOutsideClick: () => !swal.isLoading(),
               }).then()
          }
     }

     // Common layout props for the new FormField components
     const layoutProps = {
          classNameLayout: "mb-5",
          classNameLabel: "col-lg-4 col-form-label fw-bold fs-6",
          classNameField: "col-lg-8",
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema} innerRef={formRef} enableReinitialize={true}>
               {helpers => {
                    return (
                         <Form noValidate autoComplete="off">
                              {/* In case of errors */}
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              {/* Titre sauvegarde */}
                              {pInialValues?.typeHistoriqueOuSauvegarde === CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_TYPE_SAUVEGARDE && pInialValues.id && (
                                   <>
                                        <MyInputFormField
                                             label="Titre de la sauvegarde"
                                             name="titreSauvegarde"
                                             formikProps={helpers}
                                             placeholder="Titre de la sauvegarde"
                                             validationSchema={schema}
                                             {...layoutProps}
                                             classNameLayout="row" // Override mb-5 to match previous design or just use default
                                        />
                                        <div className="separator bg-primary my-5"></div>
                                   </>
                              )}

                              {/* Keywords */}
                              {/* We wrap this in a div to handle the scroll ref target */}
                              <div ref={lessFiltersDivScrollTo}>
                                   <MySelectCreatableFormField
                                        label="Mots clé"
                                        name="searchKeywords"
                                        formikProps={helpers}
                                        placeholder="Tapez vos mots clés"
                                        options={[]}
                                        isMulti
                                        onChangeCallback={handleChangeKeywordsField}
                                        validationSchema={schema}
                                        {...layoutProps}
                                   />
                              </div>

                              {/* Search Scope & Operator Logic (Conditional) */}
                              {helpers.values.searchKeywords && helpers.values.searchKeywords.length > 0 && (
                                   <>
                                        {/* Since these are conditional and were previously nested under the keywords field,
                                            we can render them as separate fields but we might want to offset them visually
                                            or just render them as standard rows. Here I render them as standard rows. */}

                                        <MySelectFormField
                                             label="Périmètre de recherche"
                                             name="searchScope"
                                             formikProps={helpers}
                                             options={CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORD_TARGETS_OPTIONS_FOR_SELECT2}
                                             placeholder="Choisissez le périmètre de recherche"
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {helpers.values.searchScope === CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_KEYWORDTARGET_CV && (
                                             <MySelectFormField
                                                  label="Opérateur logique"
                                                  name="searchOperateurLogiqueOnlyScopeCurriculum"
                                                  formikProps={helpers}
                                                  options={CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_OPERATEURS_LOGIQUE_OPTIONS_FOR_SELECT2}
                                                  placeholder="Choisissez un opérateur logique"
                                                  validationSchema={schema}
                                                  {...layoutProps}
                                             />
                                        )}
                                   </>
                              )}

                              {/* Disponibilité */}
                              <MySelectFormField
                                   label="Disponibilité"
                                   name="disponibilite"
                                   formikProps={helpers}
                                   options={CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2}
                                   placeholder="Sélectionner"
                                   validationSchema={schema}
                                   {...layoutProps}
                              />

                              {helpers.values.disponibilite === CONST_DISPONIBILITE_DATE_EXACTE && (
                                   // Keeping old component as no replacement provided
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Date de disponibilité</label>
                                        <div className="col-lg-8">
                                             <MyDateDayMonthYearField
                                                  name={"disponibiliteDate"}
                                                  value={helpers.values.disponibiliteDate}
                                                  placeholder={"Choisissez votre date"}
                                                  onChange={val => helpers.setFieldValue("disponibiliteDate", val)}
                                                  isInvalid={!!helpers.errors.disponibiliteDate}
                                             />
                                             {helpers.errors.disponibiliteDate && <div className={"text-danger"}>{helpers.errors.disponibiliteDate}</div>}
                                        </div>
                                   </div>
                              )}

                              {/* Mobilité */}
                              <MySelectFormField
                                   label="Mobilité"
                                   name="mobilite"
                                   formikProps={helpers}
                                   options={CONST_MOBILITE_OPTIONS_FOR_SELECT2}
                                   placeholder="Sélectionner"
                                   validationSchema={schema}
                                   {...layoutProps}
                              />

                              {helpers.values.mobilite === CONST_MOBILITE_DEPARTEMENTAL && (
                                   <MySelectSearchDataFromServerFormField
                                        label="Départements"
                                        name="mobiliteDepartements"
                                        formikProps={helpers}
                                        endpoint={CONST_API_UTILS_GET_DEPARTEMENTS_URL_ENDPOINT}
                                        method="GET"
                                        minimumLengthSearch={3}
                                        isMulti
                                        defaultOptions={(pInialValues?.mobiliteDepartements || []).map(item => ({
                                             label: item.nom,
                                             value: item.id,
                                        }))}
                                        placeholder="Rechercher..."
                                        validationSchema={schema}
                                        {...layoutProps}
                                   />
                              )}

                              {helpers.values.mobilite === CONST_MOBILITE_REGIONAL && (
                                   <MySelectGetAllDataFromServerFormField
                                        label="Régions"
                                        name="mobiliteRegions"
                                        formikProps={helpers}
                                        endpoint={CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT}
                                        method="GET"
                                        isMulti
                                        defaultOptions={(pInialValues.mobiliteRegions || []).map(item => ({
                                             label: item.nom,
                                             value: item.id,
                                        }))}
                                        placeholder="Sélectionner..."
                                        validationSchema={schema}
                                        {...layoutProps}
                                   />
                              )}

                              {helpers.values.mobilite === CONST_MOBILITE_PAR_VILLE && (
                                   <MySelectSearchDataFromServerFormField
                                        label="Villes"
                                        name="mobiliteVilles"
                                        formikProps={helpers}
                                        endpoint={CONST_API_UTILS_GET_VILLES_URL_ENDPOINT}
                                        method="GET"
                                        minimumLengthSearch={3}
                                        isMulti
                                        defaultOptions={(pInialValues?.mobiliteVilles || []).map(item => ({
                                             label: item.nom as string,
                                             value: item.id as number,
                                        }))}
                                        placeholder="Rechercher..."
                                        validationSchema={schema}
                                        {...layoutProps}
                                   />
                              )}

                              {/* Niveau d'études */}
                              <MySelectFormField
                                   label="Niveau d'études"
                                   name="niveauEtudes"
                                   formikProps={helpers}
                                   options={CONST_NIVEAU_ETUDES_OPTIONS_FOR_SELECT2}
                                   placeholder="Sélectionner"
                                   validationSchema={schema}
                                   {...layoutProps}
                              />

                              {/* Fraîcheur CV */}
                              <MySelectFormField
                                   label="Fraîcheur du CV"
                                   name="fraicheurCV"
                                   formikProps={helpers}
                                   options={CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_FRAICHEURS_CV_OPTIONS_FOR_SELECT2}
                                   placeholder="Sélectionner"
                                   validationSchema={schema}
                                   {...layoutProps}
                              />

                              <MyCheckboxFormField label="Avec suivi" name="isAvecSuivi" formikProps={helpers} validationSchema={schema} {...layoutProps} />

                              {helpers.values.isAvecSuivi && (
                                   <>
                                        <div className={"p-4 border border-dashed my-4"}>
                                             <h5 className={"mb-4 text-muted"}>Avec suivi</h5>
                                             <div className="separator separator-dashed mb-6" />
                                             <MySelectFormField
                                                  label="Type suivi"
                                                  name="avecSuiviType"
                                                  placeholder="Sélectionner..."
                                                  options={CONST_CANDIDAT_SUIVI_OPTIONS}
                                                  formikProps={helpers}
                                                  {...layoutProps}
                                             />

                                             <MySelectGetAllDataFromServerFormField
                                                  label="Responsables suivi"
                                                  name="avecSuiviPersonnels"
                                                  placeholder="Sélectionner..."
                                                  endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                                  defaultOptions={(pInialValues?.avecSuiviPersonnels || []).map(item => ({
                                                       label: item.prenomNom!,
                                                       value: item.id!,
                                                  }))}
                                                  method="GET"
                                                  isMulti
                                                  formikProps={helpers}
                                                  {...layoutProps}
                                             />

                                             <div className="row mb-4">
                                                  <label className="col-lg-4 col-form-label fw-bolder fs-6">Période suivi</label>

                                                  <div className="col-lg-8">
                                                       <div className="d-flex">
                                                            <MyDateDayMonthYearFormField
                                                                 name={"avecSuiviDateDebut"}
                                                                 placeholder={"Début"}
                                                                 formikProps={helpers}
                                                                 areWeekendsDisabled={false}
                                                            />
                                                            <span className="input-group-text fas fa-ellipsis d-flex align-items-center rounded-0" />
                                                            <MyDateDayMonthYearFormField
                                                                 name={"avecSuiviDateFin"}
                                                                 placeholder={"Fin"}
                                                                 formikProps={helpers}
                                                                 areWeekendsDisabled={false}
                                                            />
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   </>
                              )}

                              {/* Liste traitement */}
                              <MyCheckboxFormField
                                   label="Liste traitement"
                                   name="areCandidatesInListeDeTraitement"
                                   formikProps={helpers}
                                   validationSchema={schema}
                                   {...layoutProps}
                              />

                              <div className="separator border-primary w-25 mb-2" ref={moreFiltersDivScrollTo}></div>

                              <div className="text-primary font-weight-bold cursor-pointer" onClick={() => setMoreFiltersVisible(prev => !prev)}>
                                   {moreFiltersVisible ? "-" : "+"} de filtres
                              </div>

                              {moreFiltersVisible && (
                                   <div className={"animation-when-something-becomes-visible mt-8"}>
                                        {/* Replace the old Blacklist checkbox with this: */}
                                        {/* Section Blacklist dans plus de filtres */}
                                        <div className="row mb-5">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6">Blacklist</label>
                                             {/*<MySelectFormField name={""} formikProps={helpers} options={[
                                                  {label: "fsdfdf", value: "sdfdsf"}
                                             ]} placeholder={""} />*/}
                                             <div className="col-lg-8">
                                                  <div className="btn-group w-100 shadow-sm mb-3" role="group">
                                                       <button
                                                            type="button"
                                                            className={clsx(
                                                                 "btn btn-sm fw-bold btn-active-light-primary border",
                                                                 helpers.values.blacklistStatus === "no" ? "btn-primary" : "btn-light"
                                                            )}
                                                            onClick={() => helpers.setFieldValue("blacklistStatus", "no")}
                                                       >
                                                            <i className="bi bi-check-circle fs-4 me-2"></i> Non
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className={clsx(
                                                                 "btn btn-sm fw-bold btn-active-light-primary border",
                                                                 helpers.values.blacklistStatus === "all" ? "btn-primary" : "btn-light"
                                                            )}
                                                            onClick={() => helpers.setFieldValue("blacklistStatus", "all")}
                                                       >
                                                            <i className="bi bi-people fs-4 me-2"></i> Tous
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className={clsx(
                                                                 "btn btn-sm fw-bold btn-active-light-danger border",
                                                                 helpers.values.blacklistStatus === "yes" ? "btn-danger" : "btn-light"
                                                            )}
                                                            onClick={() => helpers.setFieldValue("blacklistStatus", "yes")}
                                                       >
                                                            <i className="bi bi-slash-circle fs-4 me-2"></i> Oui
                                                       </button>
                                                  </div>

                                                  {/* Nouveau champ Responsable : Apparaît uniquement si "Oui" est sélectionné */}
                                                  {helpers.values.blacklistStatus === "yes" && (
                                                       <div className="animation-when-something-becomes-visible">
                                                            <MySelectGetAllDataFromServerFormField
                                                                 label="Blacklisté par"
                                                                 name="blacklistPersonnels"
                                                                 placeholder="Sélectionner les responsables..."
                                                                 endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                                                 method="GET"
                                                                 isMulti
                                                                 defaultOptions={(pInialValues?.blacklistPersonnels || []).map(item => ({
                                                                      label: item.prenomNom!,
                                                                      value: item.id!,
                                                                 }))}
                                                                 formikProps={helpers}
                                                                 classNameLayout="mb-3"
                                                                 classNameLabel="fw-bold fs-7 text-muted" // Label plus discret car imbriqué
                                                                 classNameField="col-lg-12" // Prend toute la largeur de la colonne parente
                                                            />
                                                       </div>
                                                  )}

                                                  <div className="form-text text-muted">
                                                       {helpers.values.blacklistStatus === "no" && "Exclure les candidats blacklistés."}
                                                       {helpers.values.blacklistStatus === "all" && "Afficher tout le monde."}
                                                       {helpers.values.blacklistStatus === "yes" && "Afficher uniquement les candidats blacklistés."}
                                                  </div>
                                             </div>
                                        </div>

                                        {/* Viviers */}
                                        <MySelectGetAllDataFromServerFormField
                                             label="Viviers"
                                             name="viviers"
                                             formikProps={helpers}
                                             endpoint={CONST_API_UTILS_GET_VIVIERS_ENDPOINT}
                                             method="GET" // Assuming GET based on previous component, payload sent as query params
                                             payload={{ includeMyOwnViviers: true, includeTeamViviers: true }}
                                             isMulti
                                             defaultOptions={(pInialValues?.viviers || []).map(item => ({
                                                  label: item.intitule as string,
                                                  value: item.id as number,
                                             }))}
                                             placeholder="Sélectionner..."
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Offers */}
                                        <MySelectSearchDataFromServerFormField
                                             label="Annonces"
                                             name="annonces"
                                             formikProps={helpers}
                                             endpoint={CONST_API_UTILS_GET_OFFERS_ENDPOINT}
                                             method="GET"
                                             minimumLengthSearch={3}
                                             isMulti
                                             defaultOptions={(pInialValues?.annonces || []).map(item => ({
                                                  label: item.intitule as string,
                                                  value: item.id as number,
                                             }))}
                                             placeholder="Rechercher..."
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Candidats */}
                                        <MySelectSearchDataFromServerFormField
                                             label="Candidats"
                                             name="candidats"
                                             formikProps={helpers}
                                             endpoint={CONST_API_UTILS_GET_CANDIDATES_URL_ENDPOINT}
                                             method="GET"
                                             minimumLengthSearch={3}
                                             isMulti
                                             defaultOptions={(pInialValues?.candidats || []).map(item => ({
                                                  label: item.prenomNom as string,
                                                  value: item.id as number,
                                             }))}
                                             placeholder="Rechercher..."
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Type candidat */}
                                        <MySelectFormField
                                             label="Type candidat"
                                             name="candidatType"
                                             formikProps={helpers}
                                             options={CONST_CANDIDAT_TYPES_FOR_SELECT2}
                                             placeholder="Sélectionner"
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {helpers.values.candidatType === CONST_CANDIDAT_TYPE_SAISI && (
                                             <MySelectSearchDataFromServerFormField
                                                  label="Personnes ayant saisi le candidat"
                                                  name="personnelsAyantSaisiLeCandidat"
                                                  formikProps={helpers}
                                                  endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                                  method="GET"
                                                  minimumLengthSearch={3}
                                                  isMulti
                                                  defaultOptions={(pInialValues?.personnelsAyantSaisiLeCandidat || []).map(item => ({
                                                       label: item.prenomNom as string,
                                                       value: item.id as number,
                                                  }))}
                                                  placeholder="Rechercher..."
                                                  validationSchema={schema}
                                                  {...layoutProps}
                                             />
                                        )}

                                        {/* Secteurs activité */}
                                        <MySelectGetAllDataFromServerFormField
                                             label="Secteurs d'activité"
                                             name="secteursActivite"
                                             formikProps={helpers}
                                             endpoint={CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT}
                                             method="GET"
                                             isMulti
                                             defaultOptions={(pInialValues?.secteursActivite || []).map(item => ({
                                                  label: item.nom as string,
                                                  value: item.id as number,
                                             }))}
                                             placeholder="Sélectionner..."
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Métier */}
                                        <MySelectGetAllDataFromServerFormField
                                             label="Métiers"
                                             name="metiers"
                                             formikProps={helpers}
                                             endpoint={CONST_API_UTILS_GET_METIERS_URL_ENDPOINT}
                                             method="GET"
                                             isMulti
                                             defaultOptions={(pInialValues?.metiers || []).map(item => ({
                                                  label: item.nom as string,
                                                  value: item.id as number,
                                             }))}
                                             placeholder="Sélectionner..."
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Vu */}
                                        <MySelectFormField
                                             label="Vu"
                                             name="seenOrNot"
                                             formikProps={helpers}
                                             options={CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_VISUALISATIONS_OPTIONS_FOR_SELECT2}
                                             placeholder="Sélectionner"
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />

                                        {/* Experience */}
                                        <MySelectFormField
                                             label="Expérience min."
                                             name="experienceMin"
                                             formikProps={helpers}
                                             options={CONST_CANDIDAT_EXPERIENCES_OPTIONS_FOR_SELECT2}
                                             placeholder="Sélectionner"
                                             validationSchema={schema}
                                             {...layoutProps}
                                        />
                                   </div>
                              )}

                              <div className="row mt-5">
                                   <div className="col-6">
                                        <button
                                             className={"btn btn-sm btn-outline btn-outline-info d-inline-flex align-items-center"}
                                             type={"button"}
                                             disabled={helpers.isSubmitting || isSavingFilters}
                                             onClick={() => handleSauvegarde(helpers)}
                                        >
                                             {pInialValues?.typeHistoriqueOuSauvegarde === CONST_PERSONNEL_RECHERCHE_CANDIDAT_HISTORIQUE_SAUVEGARDE_TYPE_SAUVEGARDE &&
                                             pInialValues.id ? (
                                                  <>
                                                       <span className={"d-none d-md-block"}>Appliquer les modifications</span>
                                                       {!isSavingFilters ? (
                                                            <i className={"fas fa-check-circle ms-md-1"} />
                                                       ) : (
                                                            <span className="spinner-border spinner-border-sm align-middle ms-md-1" />
                                                       )}
                                                  </>
                                             ) : (
                                                  <>
                                                       <span className={"d-none d-md-block"}>Créer une sauvegarde</span>
                                                       {!isSavingFilters ? (
                                                            <i className={"fas fa-save ms-md-1"} />
                                                       ) : (
                                                            <span className="spinner-border spinner-border-sm align-middle ms-md-1" />
                                                       )}
                                                  </>
                                             )}
                                        </button>
                                   </div>
                                   <div className={"col-6 text-end"}>
                                        <button
                                             className={"btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center me-2"}
                                             type={"button"}
                                             disabled={helpers.isSubmitting || isSavingFilters}
                                             onClick={() => {
                                                  helpers.resetForm()
                                                  callBackOnReset()
                                             }}
                                        >
                                             <i className={"fas fa-sync"} />
                                        </button>
                                        <button
                                             className={"btn btn-sm btn-primary d-inline-flex align-items-center"}
                                             type={"submit"}
                                             disabled={helpers.isSubmitting || isSavingFilters}
                                        >
                                             {!helpers.isSubmitting ? <i className={"fas fa-search"} /> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default FormRechercheCandidat
